import { resolvedTypography, Typography, TypographyStyle } from '../../../design-tokens';

// One entry per extracted style, so a style the scale gains is a compile error here until it has a row.
// Order and text are the Updated frame's "Specifications for Design System" table (Typography
// 13608:135688, Type scale 13608:135854), which shows all 28 styles.
export const styleLabels: Readonly<Record<TypographyStyle, string>> = {
  display1: 'Display',
  display2: 'Display 2',
  h1Sb: 'H1 SB',
  h1: 'H1',
  h2Sb: 'H2 SB',
  h2: 'H2',
  h3Sb: 'H3 SB',
  h3: 'H3',
  h4Sb: 'H4 SB',
  h4: 'H4',
  h5: 'H5',
  h5Sb: 'H5 SB',
  paragraphLarge: 'Paragraph Large',
  paragraphLargeM: 'Paragraph Large M',
  paragraphMedium: 'Paragraph Medium',
  paragraphMediumM: 'Paragraph Medium M',
  paragraphSmall: 'Paragraph Small',
  paragraphSmallM: 'Paragraph Small M',
  body14: 'Body 14',
  body14M: 'Body 14 M',
  body14Sb: 'Body 14 SB',
  body12: 'Body 12',
  body12M: 'Body 12 M',
  bodySmall: 'Body Small',
  bodyOverline: 'Body overline',
  bodyFootnote: 'Body Footnote',
  actions14Sb: 'Actions 14 SB',
  actions12Sb: 'Actions 12 SB',
};

export const typeScaleStyles = Object.keys(styleLabels) as TypographyStyle[];

// The "Font sizes" table (Fonts sizes table 13608:135763) shows 16 of the styles, in its own order and with
// the Figma style names as labels.
export const fontSizeRows: readonly { style: TypographyStyle; label: string }[] = [
  { style: 'h1Sb', label: 'H1 SB' },
  { style: 'h2Sb', label: 'H2 SB' },
  { style: 'h2', label: 'H2' },
  { style: 'h3Sb', label: 'H3 SB' },
  { style: 'h3', label: 'H3' },
  { style: 'h4Sb', label: 'H4 SB' },
  { style: 'h4', label: 'H4' },
  { style: 'h5Sb', label: 'H5 SB' },
  { style: 'h5', label: 'H5' },
  { style: 'paragraphMediumM', label: 'Paragraph/Medium M' },
  { style: 'paragraphMedium', label: 'Paragraph/Medium' },
  { style: 'paragraphSmallM', label: 'Paragraph/Small M' },
  { style: 'paragraphSmall', label: 'Paragraph/Small' },
  { style: 'bodySmall', label: 'Body/Small' },
  { style: 'bodyOverline', label: 'Body/Overline' },
  { style: 'bodyFootnote', label: 'Body/Footnote' },
];

const WEIGHT_NAMES: Readonly<Record<number, string>> = {
  400: 'regular',
  500: 'medium',
  600: 'semibold',
  700: 'bold',
};

// The "Font sizes" table writes the weight in title case, the type scale table in lower case.
const WEIGHT_TITLES: Readonly<Record<number, string>> = {
  400: 'Regular',
  500: 'Medium',
  600: 'Semi Bold',
  700: 'Bold',
};

export interface StyleDescription {
  family: string;
  weight: string;
  weightTitle: string;
  size: string;
  lineHeight: string;
  tracking: string;
}

const px = (value: string) => parseFloat(value);

// `+ 0` turns a -0 into 0, so a style without tracking reads "0%".
const percent = (value: number) => Number(value.toFixed(2)) + 0;

// The tracking label follows the frame, which writes a percentage of the font size; the token holds px (DI-004),
// so the percentage is computed from it, never typed.
export const describeStyle = (
  style: TypographyStyle,
  tokens: Typography = resolvedTypography,
): StyleDescription => {
  const token = tokens[style];
  const weight = Number(token.fontWeight);

  return {
    family: token.fontFamily,
    weight: WEIGHT_NAMES[weight] ?? String(weight),
    weightTitle: WEIGHT_TITLES[weight] ?? String(weight),
    size: `${px(token.fontSize)} px`,
    lineHeight: `${px(token.lineHeight)} px`,
    tracking: `${percent((px(token.letterSpacing) / px(token.fontSize)) * 100)}%`,
  };
};
